import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

const WIDTH = 288;
const GAP = 6;

/**
 * Small "i" next to a figure that explains how it is worked out. Opens on
 * hover, keyboard focus or tap. Rendered in a portal so table / modal
 * overflow never clips it. Safe inside clickable cards: its clicks don't
 * reach the parent.
 *
 * help: { title, body, formula?, example? } — see builder/utils/invoiceHelp.js
 */
const InfoTip = ({ help, className = '', size = 14 }) => {
  const anchorRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [pinned, setPinned] = useState(false);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(
      Math.max(8, r.left + r.width / 2 - WIDTH / 2),
      window.innerWidth - WIDTH - 8
    );
    // Prefer below; flip above when there is no room.
    const below = r.bottom + GAP;
    const above = window.innerHeight - r.top + GAP;
    setPos(
      r.bottom + 220 > window.innerHeight && r.top > 220
        ? { left, bottom: above }
        : { left, top: below }
    );
  }, []);

  const hide = useCallback(() => {
    setPinned(false);
    setPos(null);
  }, []);

  useEffect(() => {
    if (!pos) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') hide();
    };
    const onDown = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) hide();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [pos, hide]);

  if (!help) return null;

  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <span
      ref={anchorRef}
      role="button"
      tabIndex={0}
      aria-label={`How ${help.title} is worked out`}
      onMouseEnter={place}
      onMouseLeave={() => {
        if (!pinned) setPos(null);
      }}
      onFocus={place}
      onBlur={() => {
        if (!pinned) setPos(null);
      }}
      onClick={(e) => {
        stop(e);
        if (pinned) hide();
        else {
          setPinned(true);
          place();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          stop(e);
          setPinned(true);
          place();
        }
      }}
      className={`inline-flex shrink-0 cursor-help items-center align-middle text-gray-400 hover:text-gray-600 focus:text-gray-600 focus:outline-none ${className}`}>
      <Info width={size} height={size} aria-hidden="true" />
      {pos
        ? createPortal(
            <div
              role="tooltip"
              onMouseDown={(e) => e.stopPropagation()}
              style={{ position: 'fixed', width: WIDTH, zIndex: 10000, ...pos }}
              className="rounded-lg border border-gray-200 bg-white p-3 text-left text-xs font-normal normal-case tracking-normal text-gray-700 shadow-lg">
              <div className="mb-1 text-sm font-semibold text-gray-900">{help.title}</div>
              <p className="leading-snug">{help.body}</p>
              {help.formula ? (
                <p className="mt-2 rounded bg-gray-50 px-2 py-1.5 font-mono text-[11px] leading-snug text-gray-800">
                  {help.formula}
                </p>
              ) : null}
              {help.example ? (
                <p className="mt-2 leading-snug text-gray-500">
                  <span className="font-medium text-gray-600">Example: </span>
                  {help.example}
                </p>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

export default InfoTip;
